import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import { useSocket } from './SocketContext';
import { useTranslation } from 'react-i18next';

const NotificationContext = createContext();

export const useNotification = () => useContext(NotificationContext);

const TOAST_ICONS = {
  customer: { fn: 'info', icon: '👤' },
  vehicle: { fn: 'info', icon: '🚗' },
  jobcard: { fn: 'success', icon: '📋' },
  bay: { fn: 'info', icon: '🅿️' },
  inquiry: { fn: 'info', icon: '💬' },
  service: { fn: 'info', icon: '🛠️' },
};

export const NotificationProvider = ({ children }) => {
  const { t } = useTranslation('notifications');
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const { socket } = useSocket();
  const audioRef = useRef(null);
  const stopTimerRef = useRef(null);

  // Cleanup only. The Audio object itself is created on first play, NOT here:
  // `new Audio(src)` implies preload="auto", so constructing it on mount made
  // every visitor download notification-sound.mp3 (470 KB) before they had
  // done anything — including anonymous users on the landing page, since this
  // provider wraps the whole app. useOrderSound.js already defers correctly.
  useEffect(() => () => {
    if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.loop = false;
    }
  }, []);

  const playNotificationSound = useCallback((durationMs = 2000) => {
    if (!audioRef.current) {
      audioRef.current = new Audio('/notification-sound.mp3');
      audioRef.current.volume = 0.5;
    }
    const audio = audioRef.current;
    if (!audio) return;
    if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
    audio.loop = true;
    audio.currentTime = 0;
    audio.play().catch(() => {/* autoplay blocked */});
    stopTimerRef.current = setTimeout(() => {
      audio.pause();
      audio.loop = false;
      audio.currentTime = 0;
      stopTimerRef.current = null;
    }, durationMs);
  }, []);

  const addNotification = useCallback((notification) => {
    const newNotification = {
      id: Date.now(),
      ...notification,
      read: false,
      timestamp: new Date(),
    };

    setNotifications((prev) => [newNotification, ...prev].slice(0, 50));
    setUnreadCount((prev) => prev + 1);
    playNotificationSound();

    const message = `${notification.title}: ${notification.message}`;
    const variant = TOAST_ICONS[notification.type];
    if (variant) toast[variant.fn](message, { icon: variant.icon });
    else toast.info(message);
  }, [playNotificationSound]);

  const markAsRead = useCallback((id) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnreadCount((prev) => Math.max(0, prev - 1));
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  useEffect(() => {
    if (!socket) return;

    const vLabel = (d) => `${d.make || ''} ${d.model || ''} (${d.plateNumber || ''})`.trim();

    // Event -> what to show. Titles and messages are catalogue keys, so a
    // notification arrives in whatever language the user is reading; `vars`
    // pulls the interpolation values out of the socket payload.
    const EVENTS = {
      // Customer
      'new-customer': ['customer', 'customerCreated', 'customerAdded', (d) => ({ name: d.customerName })],
      'customer-updated': ['customer', 'customerUpdated', 'customerUpdated', (d) => ({ name: d.customerName })],
      'customer-deleted': ['customer', 'customerDeleted', 'customerRemoved', (d) => ({ name: d.customerName })],
      // Vehicle
      'new-vehicle': ['vehicle', 'vehicleCreated', 'vehicleAdded', (d) => ({ vehicle: vLabel(d) })],
      'vehicle-updated': ['vehicle', 'vehicleUpdated', 'vehicleUpdated', (d) => ({ vehicle: vLabel(d) })],
      'vehicle-deleted': ['vehicle', 'vehicleDeleted', 'vehicleRemoved', (d) => ({ vehicle: vLabel(d) })],
      // Jobcard
      'new-jobcard': ['jobcard', 'jobcardCreated', 'jobcardCreated', (d) => ({ jobNo: d.jobNo })],
      'jobcard-updated': ['jobcard', 'jobcardUpdated', 'jobcardUpdated', (d) => ({ jobNo: d.jobNo })],
      'jobcard-status-changed': ['jobcard', 'jobcardStatus', 'jobcardStatus', (d) => ({ jobNo: d.jobNo, status: d.status })],
      'jobcard-deleted': ['jobcard', 'jobcardDeleted', 'jobcardRemoved', (d) => ({ jobNo: d.jobNo })],
      'jobcard-assigned': ['jobcard', 'jobcardAssigned', 'jobcardAssigned', (d) => ({ jobNo: d.jobNo })],
      // Bay
      'bay-created': ['bay', 'bayCreated', 'bayCreated', (d) => ({ name: d.name })],
      'bay-updated': ['bay', 'bayUpdated', 'bayUpdated', (d) => ({ name: d.name })],
      'bay-deleted': ['bay', 'bayDeleted', 'bayRemoved', (d) => ({ name: d.name })],
      'bay-jobcard-added': ['bay', 'bayAssigned', 'bayLinked', (d) => ({ name: d.name })],
      'bay-jobcard-removed': ['bay', 'bayCleared', 'bayUnlinked', (d) => ({ name: d.name })],
      // Inquiry
      'inquiry-created': ['inquiry', 'inquiryCreated', 'inquiryCreated', (d) => ({ name: d.name || t('messages.inquiryFallback') })],
      'inquiry-updated': ['inquiry', 'inquiryUpdated', 'inquiryUpdated', (d) => ({ name: d.name || t('messages.inquiryFallback') })],
      'inquiry-deleted': ['inquiry', 'inquiryDeleted', 'inquiryRemoved', (d) => ({ name: d.name || t('messages.inquiryFallback') })],
      'inquiry-status-changed': ['inquiry', 'inquiryStatus', 'inquiryStatus', (d) => ({ name: d.name || t('messages.inquiryFallback'), status: d.status })],
      'inquiry-pricequote-added': ['inquiry', 'priceQuote', 'priceQuoteAdded', (d) => ({ name: d.name || t('messages.inquiryFallback') })],
      // Service catalog
      'service-created': ['service', 'serviceCreated', 'serviceAdded', (d) => ({ category: d.category })],
      'service-updated': ['service', 'serviceUpdated', 'serviceUpdated', (d) => ({ category: d.category })],
      'service-deleted': ['service', 'serviceDeleted', 'serviceRemoved', (d) => ({ category: d.category })],
    };

    const handlers = Object.fromEntries(
      Object.entries(EVENTS).map(([event, [type, titleKey, messageKey, vars]]) => [
        event,
        (d) =>
          addNotification({
            type,
            title: t(`events.${titleKey}`),
            message: t(`messages.${messageKey}`, vars(d)),
            data: d,
          }),
      ])
    );

    Object.entries(handlers).forEach(([event, fn]) => socket.on(event, fn));

    return () => {
      Object.entries(handlers).forEach(([event, fn]) => socket.off(event, fn));
    };
  }, [socket, addNotification, t]);

  const value = useMemo(() => ({
    notifications,
    unreadCount,
    addNotification,
    markAsRead,
    markAllAsRead,
    clearNotifications,
  }), [notifications, unreadCount, addNotification, markAsRead, markAllAsRead, clearNotifications]);

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};
